import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { displayVatRate, formatCurrency, isVatExempt, recommendedPrice } from "@/lib/product";

/**
 * Price is ALWAYS excl. BTW with a small label (briefing §4).
 * De adviesprijs (incl. BTW) staat er doorgestreept naast (briefing feb-2026 §2a).
 */
export const PriceBlock = ({
  product,
  size = "md",
  showVatRate = false,
  showBadge = true,
  perUnit = "",
}) => {
  const { t, i18n } = useTranslation();
  const { account } = useAuth();
  const advies = recommendedPrice(product);
  const vrijgesteld = isVatExempt(account);
  const btwTarief = displayVatRate(product, account);
  const priceClass =
    size === "lg"
      ? "text-2xl font-semibold tabular-nums"
      : "text-base font-semibold tabular-nums sm:text-lg";

  return (
    <div className="space-y-1" data-testid="price-block">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className={priceClass} data-testid="price-block-price">
          {formatCurrency(product?.verkoopprijs, i18n.language)}
        </span>
        <span className="text-xs text-muted-foreground" data-testid="price-block-excl-vat-label">
          {t("price.exclVat")}{perUnit && <span data-testid="price-block-per-unit"> / {perUnit}</span>}
        </span>
        {showVatRate && (
          <span className="text-xs text-muted-foreground" data-testid="price-block-vat-rate">
            · {vrijgesteld ? t("price.vatExempt") : t("price.vatLabel", { tarief: btwTarief })}
          </span>
        )}
        {showBadge && (
          <Badge
            variant="secondary"
            className="whitespace-normal border-primary/20 bg-primary/10 text-xs font-medium text-primary"
            data-testid="price-block-customer-badge"
          >
            {t("price.customerPrice")}
          </Badge>
        )}
      </div>
      {advies !== null && (
        <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          <span className="tabular-nums" data-testid="price-block-rrp">
            {formatCurrency(advies, i18n.language)}
          </span>
          <span data-testid="price-block-rrp-label">{t("price.rrp")}</span>
        </p>
      )}
    </div>
  );
};
